// AuthContext.js

import { SkeletonNoLoginComponent } from 'common/components';
import { AuthContextProps, useAuth } from 'oidc-react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  clearDeviceWhenSessionEnds,
  getMostPrivilegedRole,
  getRolesFromAccessToken,
} from './auth.context.business';

export type AuthContextType = {
  isAuthenticated: boolean;
  userRole: string | null;
  isLoading: boolean;
  auth: AuthContextProps;
};

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  userRole: null,
  isLoading: false,
  auth: {} as AuthContextProps,
});

export const AuthProvider = ({ children }: React.PropsWithChildren) => {
  const auth = useAuth();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<string | null>('');

  useEffect(() => {
    const accessToken = auth?.userData?.access_token;
    if (accessToken) {
      setIsAuthenticated(true);
      setUserRole(getMostPrivilegedRole(getRolesFromAccessToken(accessToken)));
    }
  }, [auth?.userData?.access_token]);

  const contextValue = useMemo(
    () => ({ isAuthenticated, userRole, isLoading: auth.isLoading, auth }),
    [isAuthenticated, userRole, auth.isLoading, auth]
  );
  useEffect(() => clearDeviceWhenSessionEnds(auth), [auth]);

  return (
    <AuthContext.Provider value={contextValue}>
      {auth?.isLoading || !auth?.userData?.access_token || userRole?.length === 0 ? (
        <SkeletonNoLoginComponent />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
export const useAuthContext = () => useContext<AuthContextType>(AuthContext);
